import { useEffect } from 'react';

import { organizationQueryKey } from '@cast/constants';
import { NOT_ORGANIZATION_MEMBER_MESSAGE } from '@cast/shared-access-control-data-access';
import { Organization } from '@cast/types';
import { getQueryParam } from '@cast/utils';

import { useNavigate } from './useNavigate';

type Params = {
  organizations: Organization[];
  currentOrganization: Organization | undefined;
};

/**
 * Checks if the org in the URL query param (?org=<id>) belongs to the user.
 * If not, redirects to /forbidden with an appropriate message.
 *
 * This guard is needed because currentOrganizationIdAtom always resolves
 * (falling back to organizations[0]) — so we must explicitly check whether
 * the requested org is accessible.
 */
export const useOrganizationAccessCheck = ({
  organizations,
  currentOrganization,
}: Params) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentOrganization || !organizations.length) {
      return;
    }

    const orgIdInQuery = getQueryParam(organizationQueryKey);
    if (!orgIdInQuery) {
      return;
    }

    const hasAccess = organizations.some((org) => org.id === orgIdInQuery);
    if (!hasAccess) {
      navigate('/forbidden', {
        state: {
          message: NOT_ORGANIZATION_MEMBER_MESSAGE,
        },
      });
    }
  }, [organizations, currentOrganization, navigate]);
};
